import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { InputAdornmentProps, keyframes } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import { Moment } from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import UserInformation from "users/information/user-information";
import { MaskedTeam } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import DraftFieldUndoButton from "work/proposals/_views/drafts/draft-field-undo-button";
import SavedDraftFields from "work/proposals/_views/drafts/saved-draft-fields";
import { TextInput } from "work/proposals/_views/drafts/text-input/text-input";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import {
  fieldHasPendingChange,
  pendingChangeContainerStyles,
  pendingChangeOutlinedFieldStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import WorkAgreementLinks from "work/proposals/_views/_shared/work-agreement-links";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientSelector from "work/work-agreements/teams/client-team/_views/client-selector";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import Proposal from "work/proposals/proposal";
import {
  ReviewerEditLockAdornment,
  ReviewerEditLockIcon,
} from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import {
  proposalDetailsColumnGap,
  proposalDetailsControlMinimumWidth,
  proposalDetailsGridTemplateColumns,
} from "work/proposals/_views/_shared/proposal-details-layout";
import {
  paneIsCompact,
  workPaneProseInset,
  workPaneStretchedProseInset,
} from "work/_views/work-pane-layout";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "hasStatus",
})<{ hasStatus?: boolean }>(({ hasStatus, theme }) => ({
  [paneIsCompact(theme)]: {
    gridTemplateColumns: "1fr",
    rowGap: theme.spacing(1),
    // One stretched column, so every field reaches the row's far end and needs the inset there too
    // — see `workPaneStretchedProseInset`.
    ...workPaneStretchedProseInset(theme),
  },
  // This tab is all fields and no table, so the whole of it is prose — see `workPaneProseInset`.
  // Flush, the first field's border box touched the navigation drawer and its focus ring had
  // nowhere to go.
  ...workPaneProseInset(theme),
  alignItems: hasStatus ? "start" : "center",
  display: "grid",
  columnGap: proposalDetailsColumnGap,
  rowGap: theme.spacing(2),
  gridTemplateColumns: proposalDetailsGridTemplateColumns(theme.spacing(50)),
  justifyContent: "start",
  width: "100%",
}));
const StatusSection = styled("div")(({ theme }) => ({
  alignItems: "flex-end",
  alignSelf: "stretch",
  display: "flex",
  gridColumn: "1 / -1",
  paddingTop: theme.spacing(2),
}));
const GeneralSection = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    rowGap: 0,
  },
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));
const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
  "& .MuiFormControl-root": {
    marginTop: theme.spacing(1),
  },
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [paneIsCompact(theme)]: {
    marginBottom: theme.spacing(1),
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  width: "min-content",
}));
const NameTextField = styled(TextInput, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "pendingChange",
})<{ active?: boolean; pendingChange?: boolean }>(({ theme, active, pendingChange }) => ({
  [paneIsCompact(theme)]: {
    margin: theme.spacing(1, 0),
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  ...(pendingChange && pendingChangeOutlinedFieldStyles(theme)),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const DescriptionTextField = styled(TextInput, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "pendingChange",
})<{
  active?: boolean;
  pendingChange?: boolean;
}>(({ theme, active, pendingChange }) => ({
  [paneIsCompact(theme)]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: proposalDetailsControlMinimumWidth(theme.spacing(50)),
  ...(pendingChange && pendingChangeOutlinedFieldStyles(theme)),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const NegotiableCheckbox = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    margin: 0,
  },
  // A row, so the checkbox's Undo sits beside it rather than under it.
  alignItems: "center",
  display: "flex",
  margin: theme.spacing(1, 0, 0),
}));
const DateContainer = styled("div")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    margin: 0
  },
  display: "flex",
  margin: theme.spacing(1, 0),
  width: "100%",
}));
const DateControlLabel = styled(FormControlLabel)(() => ({
  width: "100%",
}));
const PendingChangeControlFrame = styled("span", {
  shouldForwardProp: (prop) => prop !== "pendingChange",
})<{ pendingChange?: boolean }>(({ theme, pendingChange }) => ({
  display: "inline-flex",
  ...(pendingChange && pendingChangeContainerStyles(theme)),
}));
const PendingChangeDateFieldFrame = styled("span", {
  shouldForwardProp: (prop) => prop !== "pendingChange",
})<{ pendingChange?: boolean }>(({ theme, pendingChange }) => ({
  display: "inline-flex",
  position: "relative",
  ...(pendingChange && pendingChangeContainerStyles(theme)),
}));
const DateFieldButtons = styled("span")(() => ({
  alignItems: "center",
  flexDirection: "row",
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  "& > :first-of-type": {
    marginRight: 0,
  },
}));

type DateFieldAdornmentProps = InputAdornmentProps & {
  /** The field's own controls (comments, history), supplied per field through `slotProps`. */
  fieldButtons?: React.ReactNode;
};

/**
 * A date field's input adornment: the picker's own open button, plus this field's comment and history
 * controls.
 *
 * Declared at module scope, and given its per-field content as a PROP, because MUI X takes
 * `slots.inputAdornment` as an element *type* (`PickerFieldUI` passes it to `useSlotProps` as
 * `elementType`). An inline `(params) => …` is therefore a brand new component type on every render of
 * this tab, and React answers a changed type by unmounting the old subtree and mounting a fresh one —
 * taking the state of everything inside it with it. That is what made a date field's "Revert to a
 * previous value" menu vanish a beat after it opened: opening it fetches the timeline and then the
 * change authors, whose arrival re-renders this tab, which remounted the menu's own trigger. The
 * neighbouring Name and Description fields never had the bug because their controls are passed as
 * `slotProps.input.endAdornment` — elements, which reconcile, rather than a type, which does not.
 */
function DateFieldAdornment({ children, fieldButtons }: Readonly<DateFieldAdornmentProps>) {
  return (
    <DateFieldButtons>
      {children}
      {fieldButtons}
    </DateFieldButtons>
  );
}

const HighlightDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "locked",
})<{
  active?: boolean;
  locked?: boolean;
}>(({ active, locked }) => ({
  ...(active && !locked && {
    "& fieldset": {
      animation: `${pulseBorder} 1.5s ease-in-out infinite`,
    },
  }),
  ...(locked && {
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      color: "warning.main",
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
      color: "warning.main",
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
      color: "warning.main",
    },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root .MuiPickersInputBase-notchedOutline": {
      borderColor: "warning.main",
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-disabled .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-disabled .MuiPickersInputBase-notchedOutline": {
      borderColor: "warning.main",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root:hover .MuiPickersInputBase-notchedOutline": {
      borderColor: "warning.main",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-focused .MuiPickersInputBase-notchedOutline": {
      borderColor: "warning.main",
    },
  }),
}));

type DetailsTabProps = {
  proposal?: Proposal;
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  /** Why a field's read-only history cannot restore a value; see `proposalFieldRestoreLockReason`. */
  historyReadOnlyReason?: string;
  isRequesting?: boolean;
  isSaved: boolean;
  /** The draft as last saved, which each field's Undo returns to; undefined until it is first saved. */
  savedFields?: SavedDraftFields;
  showPendingChangeHighlights?: boolean;
  negotiationId?: string;
  viewerScope?: string;
  onProposalBuilderUpdated: (newBuilder: ProposalBuilder) => void;
  onSelectingClientChange: (isSelectingClient: boolean) => void;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const {
    proposal,
    activeReviewField,
    proposalBuilder,
    issues,
    disableEditing,
    fieldLockTooltip,
    historyReadOnlyReason,
    isRequesting,
    isSaved,
    savedFields,
    showPendingChangeHighlights,
    negotiationId,
    viewerScope,
    onProposalBuilderUpdated,
    onSelectingClientChange,
  } = props;

  const [isSelectingClient, setIsSelectingClient] = useState<boolean>(false);
  const [clientSelectorAnchor, setClientSelectorAnchor] = useState<HTMLElement>();

  const currentResponseDueBy = proposalBuilder.currentSpec.responseDueBy ?? null;
  const currentStartDate = proposalBuilder.currentSpec.workAgreement.startDate ?? null;
  const currentEndDate = proposalBuilder.currentSpec.workAgreement.endDate ?? null;
  const isNegotiable = proposalBuilder.currentSpec.negotiable.value ?? true;
  const isUsingResponseDueBy = Boolean(currentResponseDueBy);
  const isUsingStartDate = Boolean(currentStartDate);
  const isUsingEndDate = Boolean(currentEndDate);
  const clientLeaderUserId = (proposalBuilder.currentSpec.workAgreement.clientTeam as ClientTeam)?.leader?.userId;
  const cachedClientLeaderIndividual = useSelector(getIndividualByUserId(clientLeaderUserId));
  const clientLeaderIndividualQuery = useGetIndividualsByUserIdsQuery(
    { userIds: clientLeaderUserId ? [clientLeaderUserId.value] : [] },
    { skip: !clientLeaderUserId || !!cachedClientLeaderIndividual },
  );
  const queriedClientLeaderIndividual = Individual.fromJSON(
    clientLeaderIndividualQuery.data?.find((individual) => individual.userId === clientLeaderUserId?.value),
  );
  const clientLeaderIndividual = cachedClientLeaderIndividual ?? queriedClientLeaderIndividual;
  const clientLeaderIsLoading = Boolean(
    clientLeaderUserId &&
      !clientLeaderIndividual &&
      (clientLeaderIndividualQuery.isLoading || clientLeaderIndividualQuery.isFetching),
  );
  const pendingChangeFields = showPendingChangeHighlights
    ? proposalBuilder.sessionHistory?.flatMap((change) => change.field ? [change.field] : []) ?? []
    : [];
  const nameHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.Name);
  const descriptionHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.Description);
  const negotiableHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.Negotiable);
  const responseDueByHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.ResponseDueBy);
  const startDateHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.StartDate);
  const endDateHasPendingChange = fieldHasPendingChange(pendingChangeFields, ProposalField.EndDate);
  const lockAdornment =
    disableEditing && fieldLockTooltip ? <ReviewerEditLockAdornment title={fieldLockTooltip} /> : undefined;
  const checkboxLockIcon = disableEditing ? <ReviewerEditLockIcon title={fieldLockTooltip} /> : undefined;
  const lockedFieldSx = lockAdornment
    ? {
      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-disabled": {
        color: "warning.main",
      },
      "& .MuiInputLabel-root.MuiInputLabel-shrink.Mui-focused": {
        color: "warning.main",
      },
      "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root .MuiPickersInputBase-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-disabled .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-disabled .MuiPickersInputBase-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root:hover .MuiPickersInputBase-notchedOutline": {
        borderColor: "warning.main",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root.Mui-focused .MuiPickersInputBase-notchedOutline": {
        borderColor: "warning.main",
      },
    }
    : undefined;

  function getMinValidDate(compareDate: AHDate, daysToAdd: number = 0) {
    const currentDateValue = new AHDate().value;
    const compareDateValue = compareDate.value;

    return new AHDate(Math.max(currentDateValue, compareDateValue)).add(daysToAdd, "day");
  }

  function handleSelectClientClicked(event: React.MouseEvent<HTMLDivElement>) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setClientSelectorAnchor(inputElements.length > 0 ? inputElements[0] : event.currentTarget);
    setIsSelectingClient(true);
    onSelectingClientChange(true);
  }

  async function handleClientUpdated(userInfo?: UserInformation) {
    if (!userInfo?.id || !userInfo?.legalEntityId || !userInfo?.name) {
      onProposalBuilderUpdated(proposalBuilder.setClient());
    } else {
      onProposalBuilderUpdated(
        proposalBuilder.setClient(
          new EntityMember({
            legalEntityId: userInfo.legalEntityId,
            userId: userInfo.id,
          }),
        ),
      );
    }
  }

  // A revert restores a prior value through the SAME builder edit path the field's own onChange
  // uses (setName/setDescription/setResponseDueBy/…), so it flows to `queueProposalUpdate` as a
  // set/resolution op rather than a raw overwrite.
  //
  // `value` is the STORED value the timeline entry carried, never the string the menu row displayed.
  // A date's two forms differ — "2026-09-28T03:59:59.999Z" against "Sep 28, 2026" — and `new AHDate`
  // cannot parse the second, so reverting any of the three dates used to build an invalid date, fail
  // ProposalSchema validation inside `Proposal.toJSON`, and leave the field untouched.
  function applyScalarRevert(field: ProposalField, value: string) {
    if (field.isEqualTo(ProposalField.Name)) {
      onProposalBuilderUpdated(proposalBuilder.setName(new ProjectName(value)));
    } else if (field.isEqualTo(ProposalField.Description)) {
      onProposalBuilderUpdated(proposalBuilder.setDescription(new ProjectDescription(value)));
    } else if (field.isEqualTo(ProposalField.ResponseDueBy)) {
      onProposalBuilderUpdated(proposalBuilder.setResponseDueBy(new AHDate(value)));
    } else if (field.isEqualTo(ProposalField.StartDate)) {
      onProposalBuilderUpdated(proposalBuilder.setStartDate(new AHDate(value)));
    } else if (field.isEqualTo(ProposalField.EndDate)) {
      onProposalBuilderUpdated(proposalBuilder.setEndDate(new AHDate(value)));
    }
  }

  /**
   * The history affordance for a field — a revert menu for someone who may edit, a read-only history
   * popover for someone who may not.
   *
   * `ProposalFieldTimelineDropdown` is not a history viewer despite its badge: its subheader reads
   * "Revert to a previous value" and every item performs one, so gating it on edit rights is right.
   * What was wrong is that it was the ONLY affordance here, so a comment-only reviewer was shown
   * "1 unviewed change on this field" on a button they could never press — the badge advertised
   * information their role had no way to reach, on the one tab that lacked the read-only popover the
   * Conflicts, Discount, Fee Schedule and Team tabs all use.
   */
  function renderFieldTimelineDropdown(field: ProposalField) {
    if (disableEditing) {
      return (
        <ProposalFieldHistoryButton
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          field={field}
          readOnlyReason={historyReadOnlyReason}
        />
      );
    }

    return (
      <ProposalFieldTimelineDropdown
        negotiationId={negotiationId}
        viewerScope={viewerScope}
        field={field}
        // Still gated while unsaved: with no negotiation history yet there is nothing to revert to.
        disabled={!isSaved}
        onRevert={(entry) => {
          // The stored value, not the row's rendering of it — see `applyScalarRevert`.
          if (entry.nextValue !== undefined) applyScalarRevert(field, String(entry.nextValue));
        }}
      />
    );
  }

  /** The field's Undo, drawn while it differs from the saved draft — the one active proposals draw. */
  function renderFieldUndo(field: ProposalField) {
    return (
      <DraftFieldUndoButton
        field={field}
        savedFields={savedFields}
        proposalBuilder={proposalBuilder}
        disabled={disableEditing}
        onProposalBuilderUpdated={onProposalBuilderUpdated}
      />
    );
  }

  /** A field's own controls: its Undo, then its history, in the order an active proposal draws them. */
  function renderFieldButtons(field: ProposalField) {
    return (
      <>
        {renderFieldUndo(field)}
        {renderFieldTimelineDropdown(field)}
      </>
    );
  }

  /** The per-field half of {@link DateFieldAdornment} — elements, passed as data through `slotProps`. */
  function renderDateFieldButtons(field: ProposalField) {
    return renderFieldButtons(field);
  }

  function renderCheckboxControl(checkbox: React.ReactElement, showLock: boolean = true) {
    if (!showLock || !checkboxLockIcon) return checkbox;

    return (
      <Box component="span" sx={{ alignItems: "center", display: "inline-flex" }}>
        {checkboxLockIcon}
        {checkbox}
      </Box>
    );
  }

  return (
    <ProposalContainer hasStatus={Boolean(proposal)}>
      {proposal && (
        <StatusSection>
          <Typography variant="h6" sx={{ fontWeight: "normal", m: 0 }}>
            <strong>Status</strong>: {proposal?.displayStatus}
          </Typography>
        </StatusSection>
      )}
      {proposal && <WorkAgreementLinks proposal={proposal} />}
      <GeneralSection>
        {(() => {
          let clientValue: string = "";
          if (MaskedTeam.isMasked(proposalBuilder.currentSpec.workAgreement.clientTeam)) {
            clientValue = "Hidden";
          } else if (clientLeaderIndividual) {
            clientValue = clientLeaderIndividual.fullName;
          } else if (clientLeaderIsLoading) {
            clientValue = "Loading...";
          }

          return (
            <ClientTextField
              disabled={disableEditing || isSelectingClient || clientSelectorAnchor !== undefined}
              label="Client Representative"
              autoComplete="off"
              error={issues?.entries.some((entry) => entry.field === ProposalField.Client)}
              helperText={issues?.entries
                .filter((entry) => entry.field === ProposalField.Client)
                .map((entry) => entry.description)
                .join("\n")}
              required
              onClick={handleSelectClientClicked}
              sx={lockedFieldSx}
              value={clientValue}
              slotProps={{
                input: {
                  startAdornment: lockAdornment,
                  endAdornment: (
                    <>
                      {renderFieldUndo(ProposalField.Client)}
                      {!disableEditing && proposalBuilder.currentSpec.workAgreement.clientTeam && (
                        <Tooltip title="Remove Client">
                          <span>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                onProposalBuilderUpdated(proposalBuilder.setClient());
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  ),
                },
                inputLabel: {
                  shrink: Boolean(proposalBuilder.currentSpec.workAgreement.clientTeam),
                },
              }}
            />
          );
        })()}
        <NameTextField
          label="Proposal Name"
          required={true}
          disabled={disableEditing}
          active={activeReviewField?.isEqualTo(ProposalField.Name) ?? false}
          className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.Name) ?? false)}
          pendingChange={nameHasPendingChange}
          initialValue={proposalBuilder.currentSpec.workAgreement.name?.value ?? ""}
          error={issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.Name))}
          helperText={issues?.entries
            .filter((entry) => entry.field.isEqualTo(ProposalField.Name))
            .map((entry) => entry.description)
            .join("\n")}
          sx={lockedFieldSx}
          slotProps={{
            input: {
              startAdornment: lockAdornment,
              endAdornment: renderFieldButtons(ProposalField.Name),
            },
          }}
          onChange={(newValue) => {
            onProposalBuilderUpdated(proposalBuilder.setName(new ProjectName(newValue)));
          }}
        />
        <DescriptionTextField
          label="Description"
          required={true}
          multiline={true}
          rows={3}
          disabled={disableEditing}
          active={activeReviewField?.isEqualTo(ProposalField.Description)}
          className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.Description))}
          pendingChange={descriptionHasPendingChange}
          initialValue={proposalBuilder.currentSpec.workAgreement.description?.value ?? ""}
          error={issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.Description))}
          helperText={issues?.entries
            .filter((entry) => entry.field.isEqualTo(ProposalField.Description))
            .map((entry) => entry.description)
            .join("\n")}
          sx={lockedFieldSx}
          slotProps={{
            input: {
              startAdornment: lockAdornment,
              endAdornment: renderFieldButtons(ProposalField.Description),
            },
          }}
          onChange={(newValue) => {
            onProposalBuilderUpdated(proposalBuilder.setDescription(new ProjectDescription(newValue)));
          }}
        />
      </GeneralSection>
      {!isRequesting && (
        <DateSection>
          <NegotiableCheckbox>
            <PendingChangeControlFrame pendingChange={negotiableHasPendingChange}>
              <FormControlLabel
                label="Negotiable"
                control={renderCheckboxControl(
                  <Checkbox
                    color="primary"
                    checked={isNegotiable}
                    disabled={disableEditing}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      onProposalBuilderUpdated(proposalBuilder.setNegotiable(checked));
                    }}
                  />,
                )}
              />
            </PendingChangeControlFrame>
            {renderFieldUndo(ProposalField.Negotiable)}
          </NegotiableCheckbox>
          <DateContainer>
            <PendingChangeControlFrame pendingChange={!isUsingResponseDueBy && responseDueByHasPendingChange}>
              <DateControlLabel
                slotProps={{
                  typography: {
                    sx: {
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }
                  },
                }}
                label={
                  isUsingResponseDueBy ? null : <span>Set a response due date?</span>
                }
                control={renderCheckboxControl(
                  <Checkbox
                    color="primary"
                    required={false}
                    disabled={disableEditing}
                    checked={isUsingResponseDueBy}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      const newDate = checked ? new AHDate().add(1, "day") : undefined;
                      onProposalBuilderUpdated(proposalBuilder.setResponseDueBy(newDate));
                    }}
                  />,
                )}
              />
            </PendingChangeControlFrame>
            {/* Drawn here only while the date is off; while it is on, the picker carries it. */}
            {!isUsingResponseDueBy && renderFieldUndo(ProposalField.ResponseDueBy)}
            {isUsingResponseDueBy && (
              <PendingChangeDateFieldFrame pendingChange={responseDueByHasPendingChange}>
                <HighlightDatePicker
                  label="Response Due Date"
                  active={activeReviewField?.isEqualTo(ProposalField.ResponseDueBy)}
                  className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.ResponseDueBy))}
                  disabled={disableEditing}
                  locked={Boolean(lockAdornment)}
                  value={proposalBuilder.currentSpec.responseDueBy?.asMoment ?? null}
                  minDate={new AHDate().asMoment}
                  slots={{
                    inputAdornment: DateFieldAdornment,
                  }}
                  slotProps={{
                    inputAdornment: {
                      fieldButtons: renderDateFieldButtons(ProposalField.ResponseDueBy),
                    } as DateFieldAdornmentProps,
                    openPickerIcon: {
                      color: disableEditing ? "disabled" : "primary",
                    },
                    textField: {
                      error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy)),
                      helperText: issues?.entries
                        .filter((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy))
                        .map((entry) => entry.description)
                        .join("\n"),
                      sx: lockedFieldSx,
                    },
                  }}
                  onChange={(date: Moment | null) => {
                    onProposalBuilderUpdated(
                      proposalBuilder.setResponseDueBy(date ? new AHDate(date.endOf("day").toISOString()) : undefined),
                    );
                  }}
                />
              </PendingChangeDateFieldFrame>
            )}
          </DateContainer>
          <DateContainer>
            <PendingChangeControlFrame pendingChange={!isUsingStartDate && startDateHasPendingChange}>
              <DateControlLabel
                slotProps={{
                  typography: {
                    sx: {
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }
                  },
                }}
                label={
                  isUsingStartDate ? null : <span>Set a start date?</span>
                }
                control={renderCheckboxControl(
                  <Checkbox
                    color="primary"
                    disabled={disableEditing}
                    required={false}
                    checked={isUsingStartDate}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      const newDate = checked
                        ? new AHDate(
                          proposalBuilder.currentSpec.responseDueBy?.toISOString() ??
                          new AHDate().add(1, "day").toISOString(),
                        )
                        : undefined;
                      onProposalBuilderUpdated(proposalBuilder.setStartDate(newDate));
                    }}
                  />,
                )}
              />
            </PendingChangeControlFrame>
            {!isUsingStartDate && renderFieldUndo(ProposalField.StartDate)}
            {isUsingStartDate && (
              <PendingChangeDateFieldFrame pendingChange={startDateHasPendingChange}>
                <HighlightDatePicker
                  label="Start Date"
                  active={activeReviewField?.isEqualTo(ProposalField.StartDate)}
                  className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.StartDate))}
                  disabled={disableEditing}
                  locked={Boolean(lockAdornment)}
                  value={currentStartDate?.asMoment}
                  minDate={new AHDate().asMoment}
                  slots={{
                    inputAdornment: DateFieldAdornment,
                  }}
                  slotProps={{
                    inputAdornment: {
                      fieldButtons: renderDateFieldButtons(ProposalField.StartDate),
                    } as DateFieldAdornmentProps,
                    openPickerIcon: {
                      color: disableEditing ? "disabled" : "primary",
                    },
                    textField: {
                      error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.StartDate)),
                      helperText: issues?.entries
                        .filter((entry) => entry.field.isEqualTo(ProposalField.StartDate))
                        .map((entry) => entry.description)
                        .join("\n"),
                      sx: lockedFieldSx,
                    },
                  }}
                  onChange={(date: Moment | null) => {
                    const newStartDate = date?.endOf("day");
                    onProposalBuilderUpdated(
                      proposalBuilder.setStartDate(newStartDate ? new AHDate(newStartDate.toISOString()) : undefined),
                    );
                  }}
                />
              </PendingChangeDateFieldFrame>
            )}
          </DateContainer>
          <DateContainer>
            <PendingChangeControlFrame pendingChange={!isUsingEndDate && endDateHasPendingChange}>
              <DateControlLabel
                slotProps={{
                  typography: {
                    sx: {
                      alignItems: "center",
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }
                  },
                }}
                label={
                  isUsingEndDate ? null : <span>Set an end date?</span>
                }
                control={renderCheckboxControl(
                  <Checkbox
                    color="primary"
                    required={false}
                    disabled={disableEditing}
                    checked={isUsingEndDate}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      const newDate = checked
                        ? new AHDate(currentStartDate?.toISOString() ?? new AHDate().add(1, "day").toISOString())
                        : undefined;
                      onProposalBuilderUpdated(proposalBuilder.setEndDate(newDate));
                    }}
                  />,
                )}
              />
            </PendingChangeControlFrame>
            {!isUsingEndDate && renderFieldUndo(ProposalField.EndDate)}
            {isUsingEndDate && (
              <PendingChangeDateFieldFrame pendingChange={endDateHasPendingChange}>
                <HighlightDatePicker
                  label="End Date"
                  active={activeReviewField?.isEqualTo(ProposalField.EndDate)}
                  className={traverserStopClassName(activeReviewField?.isEqualTo(ProposalField.EndDate))}
                  disabled={disableEditing}
                  locked={Boolean(lockAdornment)}
                  value={currentEndDate?.asMoment}
                  minDate={
                    getMinValidDate(currentStartDate ?? currentResponseDueBy ?? new AHDate().add(1, "day"))
                      .asMoment
                  }
                  slots={{
                    inputAdornment: DateFieldAdornment,
                  }}
                  slotProps={{
                    inputAdornment: {
                      fieldButtons: renderDateFieldButtons(ProposalField.EndDate),
                    } as DateFieldAdornmentProps,
                    openPickerIcon: {
                      color: disableEditing ? "disabled" : "primary",
                    },
                    textField: {
                      error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.EndDate)),
                      helperText: issues?.entries
                        .filter((entry) => entry.field.isEqualTo(ProposalField.EndDate))
                        .map((entry) => entry.description)
                        .join("\n"),
                      sx: lockedFieldSx,
                    },
                  }}
                  onChange={(date: Moment | null) => {
                    const newEndDate = date?.endOf("day");
                    onProposalBuilderUpdated(
                      proposalBuilder.setEndDate(newEndDate ? new AHDate(newEndDate.toISOString()) : undefined),
                    );
                  }}
                />
              </PendingChangeDateFieldFrame>
            )}
          </DateContainer>
        </DateSection>
      )}
      <ClientSelector
        clientUserId={
          MaskedTeam.isMasked(proposalBuilder.currentSpec.workAgreement.clientTeam)
            ? undefined
            : proposalBuilder.currentSpec.workAgreement.clientTeam?.leader?.userId
        }
        popoverAnchor={clientSelectorAnchor}
        onPopoverClose={() => {
          setClientSelectorAnchor(undefined);
          setIsSelectingClient(false);
          onSelectingClientChange(false);
        }}
        onClientUpdated={handleClientUpdated}
      />
    </ProposalContainer>
  );
}
